import {Dispatch, SetStateAction, useEffect, useState} from "react";
import readStoredValue from "./readStoredValue";

export interface StorageRepository {
    <T>(key: string, initializer: () => T): [T, Dispatch<SetStateAction<T>>]
}

export interface NullableStorageRepository {
    <T>(key: string): [T | null, Dispatch<SetStateAction<T | null>>]
}

export default function StorageRepositoryFactory(storage: Storage):
    [StorageRepository, NullableStorageRepository] {
    function StorageRepository<T>(key: string, initializer: () => T): [T, Dispatch<SetStateAction<T>>] {
        const [getter, setter] = useState<T>(() => readStoredValue(storage, key, initializer));

        useEffect(() => {
            // Persist any concrete value — including falsy ones like false / 0 / "".
            // Only the nullable variant's "absent" state (null/undefined) is skipped: the
            // initializer supplies defaults, so there's nothing useful to write for it.
            if (getter != null) {
                try {
                    storage.setItem(key, JSON.stringify(getter));
                } catch (e) {
                    // QuotaExceededError (or storage disabled) must never crash the app — the
                    // value stays in memory for the session, it just isn't persisted.
                    console.warn(`storage: could not persist "${key}"`, e);
                }
            }
        }, [getter, key]);

        return [getter, setter,]
    }

    function NullableStorageRepository<T>(key: string): [T | null, Dispatch<SetStateAction<T | null>>] {
        return StorageRepository<T | null>(key, () => null)
    }

    return [
        StorageRepository,
        NullableStorageRepository
    ]
}
